// ---------------------------------
// client/src/pages/PdfFormsDashboard.jsx
// User landing page for PDF form instances
// ---------------------------------
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchAvailablePdfTemplates } from '../utils/pdfTemplates';
import { createPdfForm, listPdfForms } from '../utils/pdfForms';
import { formatDate } from '../utils/date';
import '../css/pdfFormsDashboard.css';

const TEXT = {
    title: 'הטפסים שלי',
    newSection: 'פתיחת טופס חדש',
    draftsSection: 'טפסים שלי',
    start: 'התחל',
    open: 'פתח',
    loading: 'טוען...',
    emptyTemplates: 'אין תבניות זמינות כרגע.',
    emptyForms: 'אין טפסים פתוחים. התחל מטופס חדש.',
    statusDraft: 'טיוטה',
    statusSubmitted: 'הוגש',
};

export default function PdfFormsDashboard() {
    const navigate = useNavigate();
    const [templates, setTemplates] = useState([]);
    const [forms, setForms] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        const load = async () => {
            try {
                setIsLoading(true);
                const [templatesData, formsData] = await Promise.all([
                    fetchAvailablePdfTemplates(),
                    listPdfForms(),
                ]);
                setTemplates(templatesData);
                setForms(formsData);
            } catch (err) {
                setError(err.response?.data?.message || 'לא ניתן לטעון טפסים.');
            } finally {
                setIsLoading(false);
            }
        };
        load();
    }, []);

    const handleStart = async (templateId) => {
        try {
            const created = await createPdfForm(templateId);
            navigate(`/pdf-forms/${created.id}`);
        } catch (err) {
            setError(err.response?.data?.message || 'לא ניתן ליצור מופע טופס.');
        }
    };

    return (
        <div className="page page--pdf-forms page--accent stack stack--lg">
            <header className="page__header">
                <h1 className="page__title">{TEXT.title}</h1>
            </header>

            {error && <div className="alert alert--danger">{error}</div>}
            {isLoading && <div className="card">{TEXT.loading}</div>}

            {!isLoading && (
                <section className="stack">
                    <h2 className="card__title">{TEXT.newSection}</h2>
                    {templates.length === 0 ? (
                        <div className="card">{TEXT.emptyTemplates}</div>
                    ) : (
                        <div className="card-grid card-grid--three">
                            {templates.map((template) => (
                                <article key={template.id} className="card template-card">
                                    <div className="stack">
                                        <h3 className="card__title">{template.name}</h3>
                                        <span className="card__meta">
                                            {template.pageCount} עמודים
                                        </span>
                                    </div>
                                    <button
                                        type="button"
                                        className="button button--primary"
                                        onClick={() => handleStart(template.id)}
                                    >
                                        {TEXT.start}
                                    </button>
                                </article>
                            ))}
                        </div>
                    )}
                </section>
            )}

            {!isLoading && (
                <section className="stack">
                    <h2 className="card__title">{TEXT.draftsSection}</h2>
                    {forms.length === 0 ? (
                        <div className="card">{TEXT.emptyForms}</div>
                    ) : (
                        <div className="card-grid card-grid--two">
                            {forms.map((form) => (
                                <article key={form.id} className="card">
                                    <div className="stack">
                                        <h3 className="card__title">{form.template?.name || 'טופס ללא שם'}</h3>
                                        <span className="card__meta">
                                            {form.status === 'submitted' ? TEXT.statusSubmitted : TEXT.statusDraft}
                                        </span>
                                        <span className="card__meta">
                                            עודכן {formatDate(form.updatedAt)}
                                        </span>
                                    </div>
                                    <button
                                        type="button"
                                        className="button button--light"
                                        onClick={() => navigate(`/pdf-forms/${form.id}`)}
                                    >
                                        {TEXT.open}
                                    </button>
                                </article>
                            ))}
                        </div>
                    )}
                </section>
            )}
        </div>
    );
}
